import { Icon, Typography } from '@globalmed/atlas-ui';
import { useTranslation } from 'react-i18next';
import styles from './KioskSessionOverrideCountdown.module.css';
import { useKioskSessionOverride } from './hooks/useKioskSessionOverride';
import classNames from 'classnames';
import { WARNING_TIME_THRESHOLD } from './constants';
import { memo, useEffect, useRef, useState } from 'react';
import { useKioskAvailabilityOverride } from './hooks/useKioskAvailabilityOverride';
import { TNullable } from '@/types';

type TTimerMode = 'session' | 'availability';

type TProps = {
  variant?: 'landing' | 'default' | 'waitingRoom' | 'encounter' | 'chats';
  text?: string;
  timerText?: string;
  onTimerChange?: (value: boolean) => void;
};

export const KioskSessionOverrideCountdown =
  /**
   * Displays a scheduled timer with a warning icon, indicating an upcoming appointment.
   * The timer shows the remaining time until the appointment and a customizable message.
   * @param props - Component props.
   * @param props.variant - Variant landing | default. default value equal 'default'
   * @param props.text - Component text
   * @param props.timerText - Timer text
   * @param props.onTimerChange - Callback on timer change: false - timer not expired, true - expired
   * @returns KioskSessionOverrideCountdown component.
   */
  memo(function KioskSessionOverrideCountdown({
    variant = 'default',
    text,
    timerText,
    onTimerChange,
  }: TProps) {
    const { t } = useTranslation();
    const timeSession = useKioskSessionOverride();
    const timeAvailability = useKioskAvailabilityOverride();
    let time: TNullable<number> = null;
    let mode: TTimerMode = 'session' as TTimerMode;
    const timeAndModeChecker = () => {
      if (timeSession === null && timeAvailability === null) {
        time = null;
        mode = 'session';
      } else if (timeSession === null && timeAvailability !== null) {
        time = timeAvailability;
        mode = 'availability';
      } else if (timeSession !== null && timeAvailability === null) {
        time = timeSession;
        mode = 'session';
      } else if (
        timeSession !== null &&
        timeAvailability !== null &&
        timeSession < timeAvailability
      ) {
        time = timeSession;
        mode = 'session';
      } else {
        time = timeAvailability;
        mode = 'availability';
      }
    };
    const modeMap = {
      session: {
        text,
        timerText,
      },
      availability: {
        text: t('kiosk.please-begin-concluding-your-session'),
        timerText: t('kiosk.you-will-be-logged-out-in'),
      },
    };
    timeAndModeChecker();
    const timeFormatted = () => {
      if (time === null) return '-';
      if (time <= 0) return '00:00';
      const minutes = Math.trunc(time / 60);
      const seconds = time % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
      if (time !== null) {
        onTimerChange?.(true);
      } else {
        onTimerChange?.(false);
      }
    }, [onTimerChange, time]);

    const isWarningVisible = time !== null && time <= WARNING_TIME_THRESHOLD;

    const warningText = [
      modeMap[mode].text,
      modeMap[mode].timerText ?? t('kiosk.time-remaining'),
      timeFormatted(),
    ]
      .filter(Boolean)
      .join(' ');

    // Announce the logout countdown to screen readers once, when the warning
    // first appears. The visible timer ticks every second, so a live region
    // tied to it would be far too noisy; instead we speak a single snapshot
    // captured on the hidden -> visible transition.
    const wasWarningVisibleRef = useRef(false);
    const [announcement, setAnnouncement] = useState('');

    useEffect(() => {
      if (isWarningVisible && !wasWarningVisibleRef.current) {
        setAnnouncement(warningText);
      } else if (!isWarningVisible) {
        setAnnouncement('');
      }
      wasWarningVisibleRef.current = isWarningVisible;
    }, [isWarningVisible, warningText]);

    if (time === null || time > WARNING_TIME_THRESHOLD) {
      return (
        <p role="status" aria-live="assertive" className={styles.srOnly}>
          {announcement}
        </p>
      );
    }

    const firstText = (() => {
      if (mode === 'session') {
        return modeMap[mode].text;
      }
      if (mode === 'availability') {
        if (variant === 'chats' || variant === 'encounter') {
          return null;
        } else {
          return modeMap[mode].text;
        }
      }
    })();
    const secondText = (() => {
      if (mode === 'session') {
        return modeMap[mode].timerText;
      }
      if (mode === 'availability') {
        if (variant === 'chats' || variant === 'encounter') {
          return `${modeMap[mode].text} ${modeMap[mode].timerText}`;
        } else {
          return modeMap[mode].timerText;
        }
      }
    })();

    return (
      <div className={classNames(styles.wrapper, styles[variant])}>
        {variant !== 'landing' && (
          <Icon
            className={styles.icon}
            name="AlertTriangle"
            size="16"
            decorative
          />
        )}
        <Typography
          className={classNames({
            [styles.topText]: variant === 'landing',
          })}
        >
          {firstText}
        </Typography>
        <Typography
          className={classNames(styles.timer, {
            [styles.bottomText]: variant === 'landing',
          })}
        >
          <span>{secondText ?? t('kiosk.time-remaining')}</span>
          <span style={{ fontWeight: 600 }}>{timeFormatted()}</span>
        </Typography>

        {/* Single, non-ticking snapshot announced to screen readers on appear */}
        <p role="status" aria-live="assertive" className={styles.srOnly}>
          {announcement}
        </p>
      </div>
    );
  });
